'use client';

import { PlayButton } from '@/components/play-button/play-button';
import { Anchor } from '@/components/ui/anchor';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { AnalyticsPageCategories } from '@/enums/analytics-type';
import { useDataAnalytics } from '@/hooks/analytics/analytics';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { MovieRewardsVideoDialog } from './movie-rewards-video-dialog';

type MembershipBenefitProps = {
  iconSrc: string;
  text: string;
};

const MembershipBenefit = ({ iconSrc, text }: Readonly<MembershipBenefitProps>): React.JSX.Element => {
  return (
    <li className="grid w-full max-w-[360px] grid-rows-[53px_auto] justify-items-center gap-sm text-center">
      <Image alt=""
        aria-hidden="true"
        className="h-[53px] w-[60px] object-contain"
        height={53}
        src={iconSrc}
        width={60}
      />
      <p className="w-full max-w-[130px] break-words text-center font-semibold md:max-w-[210px]"
        data-test={`text-movie-rewards-benefit-${text.toLowerCase().replaceAll(' ', '-')}`}
      >
        {text}
      </p>
    </li>
  );
};

export const MovieRewardsVideoWhatYouGet = (): React.JSX.Element => {
  const router = useRouter();
  const { trackClick } = useDataAnalytics();

  return (
    <section className="flex flex-col items-center gap-xl bg-white py-md sm:py-jumbo"
      data-test="section-movie-rewards-video-what-you-get"
    >
      <div className="relative aspect-video w-full max-w-[1090px]">
        <Image fill
          priority
          alt="Cinemark Movie Rewards members enjoying snacks"
          className="object-cover"
          sizes="(max-width: 1090px) 100vw, 1090px"
          src="/img/movie-rewards/movie-rewards-video-poster.png"
        />
        <MovieRewardsVideoDialog>
          <PlayButton ariaLabel="Play Cinemark Movie Rewards video"
            className="size-[100px] hover:bg-black/45"
            dataTest="button-movie-rewards-video-play"
            iconClassName="translate-x-1"
          />
        </MovieRewardsVideoDialog>
      </div>
      <h2 className="text-[34px] leading-none uppercase"
        data-test="heading-movie-rewards-what-you-get"
      >
        What You Get
      </h2>
      <div className="grid w-full max-w-[768px] grid-cols-[1fr_1px_1fr] grid-rows-[auto_auto_repeat(3,auto)_auto_auto] gap-x-0 gap-y-sm">
        <div className="row-span-7 grid grid-rows-subgrid justify-items-center pr-sm">
          <div className="contents">
            <Image alt="Cinemark Movie Fan"
              className="h-auto w-full max-w-[130px] md:max-w-[210px]"
              height={75}
              src="/img/movie-rewards/Movie_Fan_Logo.png"
              width={295}
            />
            <h4 className="text-center"
              data-test="heading-movie-fan-always-free"
            >
              Always Free
            </h4>
            <ul className="contents"
              data-test="list-movie-fan-benefits"
            >
              <MembershipBenefit iconSrc="/img/movie-rewards/movie-rewards-dollar.svg"
                text="Earn points on every purchase"
              />
              <MembershipBenefit iconSrc="/img/movie-rewards/movie-rewards-gift.svg"
                text="Redeem points for rewards"
              />
              <MembershipBenefit iconSrc="/img/movie-rewards/lock.svg"
                text="Access member-only offers"
              />
            </ul>
            <Button className="w-full max-w-[150px] md:max-w-[210px]"
              data-test="button-movie-fan-action"
              onClick={() => {
                trackClick({
                  linkCategory: AnalyticsPageCategories.MOVIE_REWARDS,
                  linkName: 'Movie Fan Join for Free',
                });
                router.push('/membership/register?returnurl=/movie-rewards-info');
              }}
            >
              Join for Free
            </Button>
            <Anchor data-test="link-movie-fan-learn-more"
              dataLinkCategory={AnalyticsPageCategories.MOVIE_REWARDS}
              dataLinkName="Movie Fan Learn More"
              href="/movie-rewards-info/movie-fan"
              variant="link"
            >
              Learn More
            </Anchor>
          </div>
        </div>
        <Separator className="row-span-7"
          orientation="vertical"
        />
        <div className="row-span-7 grid grid-rows-subgrid justify-items-center pl-sm">
          <div className="contents">
            <Image alt="Cinemark Movie Club"
              className="h-auto w-full max-w-[130px] md:max-w-[210px]"
              height={75}
              src="/img/movie-rewards/Movie_Club_Logo.png"
              width={295}
            />
            <h4 className="text-center"
              data-test="heading-movie-club-get-even-more"
            >
              Get Even More
            </h4>
            <ul className="contents"
              data-test="list-movie-club-benefits"
            >
              <MembershipBenefit iconSrc="/img/movie-rewards/ticket.svg"
                text="Get one ticket credit every month"
              />
              <MembershipBenefit iconSrc="/img/movie-rewards/cup.svg"
                text="Save 20% on concessions"
              />
              <MembershipBenefit iconSrc="/img/movie-rewards/money-star.svg"
                text="Avoid online ticket fees"
              />
            </ul>
            <Button className="w-full max-w-[150px] md:max-w-[210px]"
              data-test="button-movie-club-action"
              onClick={() => {
                trackClick({
                  linkCategory: AnalyticsPageCategories.MOVIE_REWARDS,
                  linkName: 'Movie Club Upgrade',
                });
                router.push('/movieclub');
              }}
            >
              Upgrade
            </Button>
            <Anchor data-test="link-movie-club-learn-more"
              dataLinkCategory={AnalyticsPageCategories.MOVIE_REWARDS}
              dataLinkName="Movie Club Learn More"
              href="/movieclub"
              variant="link"
            >
              Learn More
            </Anchor>
          </div>
        </div>
      </div>
    </section>
  );
};
